import React, { useEffect, useRef, useState } from "react";
import { PLAN, PREMIUM_LIVE } from "../config/plan";
import {
  OPERATOR,
  GRIEVANCE,
  POLICY_VERSION,
  LAST_UPDATED,
} from "../legal/legalMeta";
//import LegalNotice from "./LegalNotice";

import InstallAppButton from "./InstallAppButton";
import {
  FileText,
  User,
  CheckCircle,
  CreditCard,
  AlertTriangle,
  RefreshCw,
  Mail,
  Instagram,
  Youtube,
} from "lucide-react";
//Facebook
//import { FaXTwitter } from "react-icons/fa6";
// ─── Utility: intersection-observer reveal hook ───────────────────────────
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── Vybex Logo ──────────────────────────────────────────────────────────────
const VybexLogo = () => {
  const ref = useRef(null);
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Michroma&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    const container = ref.current;
    if (!container) return;
    container.innerHTML = ""; // prevent double render in StrictMode
    "VYBEX".split("").forEach((ch, i) => {
      const s = document.createElement("span");
      Object.assign(s.style, {
        fontFamily: "'Michroma', sans-serif",
        background: "linear-gradient(135deg, #00d4ff, #7c3aed, #ec4899)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        opacity: "0",
        transform: "translateY(6px) scale(0.85)",
        display: "inline-block",
        transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms`,
      });
      s.textContent = ch;
      container.appendChild(s);
      setTimeout(
        () => {
          s.style.opacity = "1";
          s.style.transform = "translateY(0) scale(1)";
        },
        100 + i * 80,
      );
    });
  }, []);
  return (
    <span
      ref={ref}
      style={{
        display: "inline-flex",
        letterSpacing: "0.18em",
        fontSize: "0.9rem",
        background: "#0a0a18",
        padding: "4px 12px",
        borderRadius: "999px",
        border: "1px solid rgba(124,58,237,0.35)",
      }}
    />
  );
};
// ─── Footer Link ──────────────────────────────────────────────────────────
const FooterLink = ({ href, onClick, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={href || "#"}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        color: hover ? "#7c3aed" : "#4b5563",
        textDecoration: "none",
        fontSize: "0.875rem",
        fontWeight: hover ? "600" : "400",
        cursor: "pointer",
        transition: "color 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
      }}
    >
      {hover && <span style={{ fontSize: "0.6rem", color: "#a78bfa" }}>▶</span>}
      {children}
    </a>
  );
};

// ─── Social Icon ──────────────────────────────────────────────────────────
const SocialIcon = ({ s }) => {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={s.href}
      target="_blank"
      rel="noopener noreferrer"
      title={s.name}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        border: hover ? `1.5px solid ${s.hoverBg}` : "1.5px solid #ede9fe",
        background: hover ? `${s.hoverBg}14` : "#fafafa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1rem",
        textDecoration: "none",
        transition: "all 0.2s",
        transform: hover ? "translateY(-2px)" : "none",
      }}
    >
      {s.icon}
    </a>
  );
};

// ─── Section Card ─────────────────────────────────────────────────────────
const SectionCard = ({ section, idx }) => {
  const [ref, visible] = useReveal();
  const [expanded, setExpanded] = useState(true);

  return (
    <div
      ref={ref}
      data-section={idx}
      style={{
        background: "#fff",
        border: "1.5px solid #ede9fe",
        borderRadius: "20px",
        overflow: "hidden",
        marginBottom: "1.25rem",
        boxShadow: "0 2px 16px rgba(102,126,234,0.05)",
        transform: visible ? "translateY(0)" : "translateY(24px)",
        opacity: visible ? 1 : 0,
        transition: `transform 0.65s cubic-bezier(.22,1,.36,1) ${idx * 60}ms, opacity 0.65s ease ${idx * 60}ms`,
      }}
    >
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        className="tos-card-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1.75rem 2rem",
          cursor: "pointer",
          userSelect: "none",
          background: expanded
            ? "linear-gradient(135deg,rgba(102,126,234,0.03),rgba(118,75,162,0.05))"
            : "#fff",
          borderBottom: expanded ? "1px solid #ede9fe" : "none",
          transition: "background 0.3s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              flexShrink: 0,
              background:
                "linear-gradient(135deg,rgba(102,126,234,0.1),rgba(118,75,162,0.16))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#7c3aed",
            }}
          >
            {section.icon}
          </div>
          <h2
            className="tos-card-title"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(1.05rem, 2vw, 1.3rem)",
              fontWeight: "700",
              color: "#1e1b4b",
              margin: 0,
              letterSpacing: "-0.01em",
            }}
          >
            {section.title}
          </h2>
        </div>
        <span
          style={{
            fontSize: "1.1rem",
            color: "#a78bfa",
            flexShrink: 0,
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
          }}
        >
          ▾
        </span>
      </div>

      {/* Body */}
      {expanded && (
        <div className="tos-card-body" style={{ padding: "1.75rem 2rem" }}>
          {section.prose && (
            <p
              style={{
                color: "#4b5563",
                lineHeight: 1.8,
                fontSize: "0.93rem",
                marginBottom: section.items ? "1.25rem" : 0,
              }}
            >
              {section.prose}
            </p>
          )}
          {section.items && (
            <>
              {section.itemsLabel && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    marginBottom: "0.85rem",
                  }}
                >
                  <div
                    style={{
                      width: "1.25rem",
                      height: "2px",
                      background: "linear-gradient(90deg,#667eea,#764ba2)",
                      borderRadius: "2px",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "0.73rem",
                      fontWeight: "700",
                      color: "#7c3aed",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                    }}
                  >
                    {section.itemsLabel}
                  </span>
                </div>
              )}
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "grid",
                  gap: "0.5rem",
                }}
              >
                {section.items.map((item, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "0.75rem",
                      padding: "0.6rem 0.85rem",
                      background: "rgba(102,126,234,0.03)",
                      borderRadius: "8px",
                      border: "1px solid rgba(196,181,253,0.3)",
                    }}
                  >
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        flexShrink: 0,
                        background: "linear-gradient(135deg,#667eea,#764ba2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.6rem",
                        color: "#fff",
                        fontWeight: "800",
                        marginTop: "0.1rem",
                      }}
                    >
                      ✓
                    </span>
                    <span
                      style={{
                        color: "#374151",
                        lineHeight: 1.65,
                        fontSize: "0.9rem",
                      }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {section.email && (
            <div style={{ marginTop: section.prose ? "1.25rem" : 0 }}>
              <a
                href={`mailto:${section.email}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.6rem",
                  background:
                    "linear-gradient(135deg,rgba(102,126,234,0.07),rgba(118,75,162,0.1))",
                  border: "1px solid #c4b5fd",
                  borderRadius: "10px",
                  padding: "0.7rem 1.25rem",
                  textDecoration: "none",
                  color: "#7c3aed",
                  fontWeight: "700",
                  fontSize: "0.9rem",
                  transition: "box-shadow 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow =
                    "0 4px 16px rgba(118,75,162,0.18)")
                }
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
              >
                <Mail size={16} /> {section.email}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Inline Footer ────────────────────────────────────────────────────────
const Footer = ({ onNavigate }) => {
  const currentYear = new Date().getFullYear();
  const handleNavigation = (view) => {
    if (onNavigate) onNavigate(view);
    else window.location.href = view.startsWith("/") ? view : `/${view}`;
  };

  const socials = [
    {
      name: "Instagram",
      icon: <Instagram size={18} color="#E1306C" />,
      href: "https://www.instagram.com/noryq.ai",
      hoverBg: "#E1306C",
    },

    {
      name: "YouTube",
      icon: <Youtube size={18} color="#aa023aff" />,
      href: "https://youtube.com/@noryq-ai",
      hoverBg: "#FF0000",
    },
  ];

  const quickLinks = [
    { name: "Home", view: "home" },
    { name: "Trending", view: "trending" },
    { name: "Premium", view: "premium" },
    { name: "About Us", href: "/about" },
    { name: "Why Us", href: "/whyus" },
  ];
  const legalLinks = [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Cookie Policy", href: "/cookies" },
    { name: "Refund Policy", href: "/refund" },
    { name: "Grievance Officer", href: "/privacy#grievance" },
    { name: "Contact Us", href: "/contact" },
  ];

  return (
    <footer
      className="pp-footer"
      style={{
        background: "#fff",
        borderTop: "1px solid #ede9fe",
        padding: "4rem 2rem 2rem",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "3rem",
            marginBottom: "3rem",
          }}
        >
          {/* Brand */}
          <div>
            <a
              href="/"
              style={{
                textDecoration: "none",
                display: "inline-block",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: "900",
                  fontSize: "1.5rem",
                  letterSpacing: "0.2em",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {/* Logo Section - Fixed Size */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    cursor: "pointer",
                    flexShrink: 0, // Prevents logo from shrinking
                  }}
                >
                  <img
                    src="/logo.png"
                    alt="NORYQ"
                    style={{
                      height: "50px",
                      width: "auto", // Maintains aspect ratio
                      display: "block",
                    }}
                  />
                </div>
              </span>
            </a>
            <p
              style={{
                color: "#6b7280",
                lineHeight: 1.7,
                fontSize: "0.875rem",
                marginBottom: 0,
              }}
            >
              Your AI-powered movie and TV show recommendation companion. Find
              your perfect watch based on your mood.
            </p>
            {/* Install CTA card */}
            <style>{`
              @media (max-width: 480px) {
                .noryq-install-card { padding: 1rem !important; }
                .noryq-install-card p { font-size: 0.88rem !important; }
                .noryq-install-label { display: none !important; }
              }
            `}</style>
            <div
              className="noryq-install-card"
              style={{
                background:
                  "linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(118,75,162,0.1) 100%)",
                border: "1.5px solid #ede9fe",
                borderRadius: "16px",
                padding: "1.1rem 1.25rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.85rem",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {/* Label row */}
              <div
                className="noryq-install-label"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              ></div>

              {/* Headline */}
              <p
                className="noryq-install-headline"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "0.95rem",
                  fontWeight: "700",
                  fontStyle: "italic",
                  color: "#1e1b4b",
                  lineHeight: 1.35,
                  margin: 0,
                }}
              >
                Take NORYQ everywhere.
                <br />
                <span
                  style={{
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Install it in one tap.
                </span>
              </p>

              {/* Button — full width, never overflows */}
              <div style={{ width: "100%", minWidth: 0 }}>
                <InstallAppButton />
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Quick Links
              </h4>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {quickLinks.map((link) => (
                <li key={link.name} style={{ marginBottom: "0.6rem" }}>
                  <FooterLink
                    href={link.href || "#"}
                    onClick={
                      link.view
                        ? (e) => {
                            e.preventDefault();
                            handleNavigation(link.view);
                          }
                        : undefined
                    }
                  >
                    {link.name}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Legal
              </h4>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {legalLinks.map((link) => (
                <li key={link.name} style={{ marginBottom: "0.6rem" }}>
                  <FooterLink href={link.href}>{link.name}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Stay Updated */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Stay Updated
              </h4>
            </div>
            <p
              style={{
                color: "#6b7280",
                fontSize: "0.875rem",
                lineHeight: 1.7,
                marginBottom: "1rem",
              }}
            >
              Follow us for the latest recommendations and feature drops.
            </p>
            {/* Socials */}
            <div
              style={{ display: "flex", gap: "0.6rem", marginBottom: "1rem" }}
            >
              {socials.map((s) => (
                <SocialIcon key={s.name} s={s} />
              ))}
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                background:
                  "linear-gradient(135deg,rgba(102,126,234,0.07),rgba(118,75,162,0.1))",
                border: "1px solid #c4b5fd",
                borderRadius: "999px",
                padding: "0.4rem 1rem",
                marginBottom: "1.25rem",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#667eea,#764ba2)",
                }}
              />
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#7c3aed",
                  fontWeight: 600,
                }}
              >
                v1.0.0 — Live
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, #c4b5fd 40%, #a78bfa 60%, transparent)",
            marginBottom: "1.5rem",
          }}
        />

        {/* Bottom */}
        <div
          className="pp-footer-bottom"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#9ca3af" }}>
            © {currentYear} NORYQ. All rights reserved. Made with ❤️ in India
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              fontSize: "0.82rem",
              color: "#9ca3af",
              alignItems: "center",
            }}
          >
            <span>Powered by</span>
            <a
              href="https://vybex1.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <VybexLogo />
            </a>

            <span style={{ color: "#c4b5fd" }}>•</span>
            <span>v1.0.0</span>
          </div>
        </div>
        {/* <LegalNotice /> */}
      </div>
    </footer>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────
const TermsOfService = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => {
    document.title = "Terms Of Service | NORYQ";
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);
  const [ctaRef, ctaVisible] = useReveal();

  const sections = [
    {
      icon: <CheckCircle size={20} />,
      title: "1. Acceptance of Terms",
      prose: `These Terms are an agreement between you and ${OPERATOR.name}, ${OPERATOR.status}, based at ${OPERATOR.address} ("NORYQ", "we"). By creating an account or using NORYQ (noryq.in) you agree to these Terms, our Privacy Policy and our Cookie Policy. If you do not agree, please do not use NORYQ.`,
    },
    {
      icon: <User size={20} />,
      title: "2. Eligibility",
      prose:
        "You must be 18 years or older and legally able to enter into a contract under Indian law to use NORYQ. If you are under 18, please do not create an account or use the service.",
    },
    {
      icon: <FileText size={20} />,
      title: "3. About The Service",
      prose:
        "NORYQ gives AI-powered movie and TV recommendations based on your mood and preferences. We offer a free plan, and a paid Premium plan is planned (see Section 6).",
      itemsLabel: "Please Note",
      items: [
        "Recommendations are generated by AI and may be inaccurate, incomplete or unsuitable. Check age ratings and content details before you watch.",
        "We do not host or stream any films or shows. Availability on streaming platforms can change, differ by region, or be wrong.",
        "NORYQ is independent and is not affiliated with or endorsed by any studio, streaming service or brand mentioned.",
        "Movie data comes from TMDB and streaming availability from JustWatch. This product uses the TMDB API but is not endorsed or certified by TMDB.",
        "Posters, titles and descriptions belong to their owners. Ads and links to other websites are not under our control, and we are not responsible for them.",
      ],
    },
    {
      icon: <User size={20} />,
      title: "4. Your Account",
      prose:
        "You are responsible for keeping your account secure. You can delete your account at any time from Privacy & Data in your account menu, or by emailing us from your registered address.",
      itemsLabel: "Your Responsibilities",
      items: [
        "Keep your login details confidential",
        "Give accurate information",
        "Everything that happens under your account",
        "Tell us immediately about unauthorised access",
      ],
    },
    {
      icon: <AlertTriangle size={20} />,
      title: "5. Acceptable Use",
      prose: "To keep NORYQ safe for everyone, you agree not to:",
      itemsLabel: "Prohibited Actions",
      items: [
        "Use the service for anything unlawful",
        "Try to gain unauthorised access to our systems or other users' accounts",
        "Interfere with or overload the service",
        "Copy, scrape or resell our recommendations or data, or use bots or automated access",
        "Impersonate anyone or share your login with others",
        "Send us content (such as feedback or messages) that is unlawful, defamatory, abusive or infringes someone's rights",
      ],
    },
    {
      icon: <CreditCard size={20} />,
      title: "6. Free And Premium Plans",
      prose: `The free plan has usage limits and may show ads (only if you agree to advertising cookies). ${
        PREMIUM_LIVE
          ? `Premium is a one-time ${PLAN.durationDays}-day pass costing Rs ${PLAN.priceInr}. It does not renew automatically.`
          : `Premium is planned but is not on sale yet. Joining the Premium waitlist is free and is not a purchase. When it launches it will be a one-time ${PLAN.durationDays}-day pass that does not renew automatically.`
      } The full price in INR is always shown before you pay, and our Refund and Cancellation Policy applies. Payments are processed by Razorpay.`,
    },
    {
      icon: <CheckCircle size={20} />,
      title: "7. Our Rights",
      prose:
        "The NORYQ name, logo, design and software belong to us. We give you a limited, personal, non-exclusive, non-transferable right to use the service. We may suspend or end accounts that break these Terms or the law, or to protect users or the service, and will tell you why where practical. You may stop using NORYQ at any time.",
    },
    {
      icon: <AlertTriangle size={20} />,
      title: "8. Disclaimers And Liability",
      prose:
        'NORYQ is provided "as is" and "as available". We do not promise it will be uninterrupted, error-free or that every recommendation will suit you. To the extent the law allows, we are not liable for indirect or consequential loss, and our total liability for any claim is limited to the amount you paid us in the previous 12 months (or Rs 1,000 if you have not paid us). Nothing in these Terms removes any right you have under the Consumer Protection Act, 2019 or any other law that cannot be excluded.',
    },
    {
      icon: <RefreshCw size={20} />,
      title: "9. Changes To These Terms",
      prose:
        "We may update these Terms. For material changes we will give notice in the app or by email, where practical at least 14 days before they take effect. If you keep using NORYQ after that, you accept the updated Terms. If you disagree, stop using NORYQ and ask us to delete your account.",
    },
    {
      icon: <FileText size={20} />,
      title: "10. Governing Law And Disputes",
      prose:
        "These Terms are governed by the laws of India. Courts at Indore, Madhya Pradesh have jurisdiction, but this does not take away your right as a consumer to approach a Consumer Commission or court that has jurisdiction where you live or work. Please contact our Grievance Officer first. Most issues can be resolved quickly.",
    },
    {
      icon: <Mail size={20} />,
      title: "11. Grievance Officer And Contact",
      prose: `Grievance Officer: ${GRIEVANCE.name} (${GRIEVANCE.designation}). Address: ${GRIEVANCE.address}. We acknowledge every complaint within ${GRIEVANCE.acknowledgeWithin} and resolve it within ${GRIEVANCE.resolveWithin}. These Terms are version ${POLICY_VERSION}, last updated ${LAST_UPDATED}.`,
      email: GRIEVANCE.email,
    },
  ];

  const navLabels = [
    "Acceptance",
    "Eligibility",
    "Service",
    "Account",
    "Acceptable Use",
    "Free & Premium",
    "Our Rights",
    "Liability",
    "Changes",
    "Law",
    "Contact",
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fafafa",
        color: "#1e1b4b",
        fontFamily: "'DM Sans', sans-serif",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700;1,900&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #f5f3ff; }
        ::-webkit-scrollbar-thumb { background: #a78bfa; border-radius: 2px; }
        @keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes float-blob { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-18px) scale(1.04)} }

        /* ── Responsive ────────────────────────────────────────── */
        @media (max-width: 480px) { .tos-nav { padding: 0 1.25rem !important; } }

        @media (max-width: 640px) {
          .tos-hero-inner   { padding: 4.5rem 1.25rem 3.5rem !important; }
          .tos-sections     { padding: 2.5rem 1.25rem !important; }
          .tos-cta-card     { padding: 2.5rem 1.5rem !important; }
          .tos-cta-btn      { width: 100% !important; }
          .tos-footer       { padding: 3rem 1.25rem 1.5rem !important; }
          .tos-footer-bottom { flex-direction: column !important; align-items: flex-start !important; gap: 0.5rem !important; }
        }

        @media (max-width: 480px) {
          .tos-quicknav    { padding: 1rem 1.25rem !important; }
          .tos-card-header { padding: 1.25rem 1.25rem !important; }
          .tos-card-body   { padding: 1.25rem 1.25rem !important; }
        }

        @media (max-width: 400px) {
          .tos-card-title  { font-size: 1rem !important; }
          .noryq-install-label { display: none !important; }
          .noryq-install-card  { padding: 1rem !important; }
        }
      `}</style>

      {/* ── NAV ──────────────────────────────────────────────────────── */}
      <nav
        className="tos-nav"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(250,250,250,0.88)",
          backdropFilter: "blur(18px)",
          borderBottom: "1px solid #ede9fe",
          padding: "0 2.5rem",
          height: "64px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <a href="/" style={{ textDecoration: "none" }}>
          <span
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: "900",
              fontSize: "1.45rem",
              letterSpacing: "0.2em",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              cursor: "pointer",
              userSelect: "none",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            {/* Logo Section - Fixed Size */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                cursor: "pointer",
                flexShrink: 0, // Prevents logo from shrinking
              }}
            >
              <img
                src="/logo.png"
                alt="NORYQ"
                style={{
                  height: "50px",
                  width: "auto", // Maintains aspect ratio
                  display: "block",
                }}
              />
            </div>
          </span>
        </a>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          background: "#fff",
          borderBottom: "1px solid #ede9fe",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-15%",
            right: "-8%",
            width: "50vw",
            height: "50vw",
            maxWidth: "620px",
            maxHeight: "620px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(102,126,234,0.09) 0%, rgba(118,75,162,0.05) 50%, transparent 70%)",
            animation: "float-blob 8s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-10%",
            left: "-6%",
            width: "36vw",
            height: "36vw",
            maxWidth: "460px",
            maxHeight: "460px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(118,75,162,0.06) 0%, transparent 70%)",
            animation: "float-blob 11s ease-in-out infinite reverse",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage:
              "radial-gradient(circle, #d8d0f8 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            opacity: 0.35,
          }}
        />

        <div
          className="tos-hero-inner"
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "7rem 2rem 6rem",
            position: "relative",
            zIndex: 1,
            textAlign: "center",
          }}
        >
          {/* Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background:
                "linear-gradient(135deg,rgba(102,126,234,0.07),rgba(118,75,162,0.12))",
              border: "1px solid #c4b5fd",
              borderRadius: "999px",
              padding: "0.4rem 1.1rem",
              marginBottom: "2rem",
              transform: heroVisible ? "translateY(0)" : "translateY(14px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.6s ease 0.1s, opacity 0.6s ease 0.1s",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                animation: "pulse-dot 1.8s infinite",
              }}
            />
            <span
              style={{
                fontSize: "0.74rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#7c3aed",
                fontWeight: 700,
              }}
            >
              Legal Agreement
            </span>
          </div>

          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2.6rem, 6vw, 5rem)",
              fontWeight: "900",
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              color: "#1e1b4b",
              marginBottom: "1.5rem",
              transform: heroVisible ? "translateY(0)" : "translateY(22px)",
              opacity: heroVisible ? 1 : 0,
              transition:
                "transform 0.75s cubic-bezier(.22,1,.36,1) 0.2s, opacity 0.75s ease 0.2s",
            }}
          >
            Terms of
            <br />
            <span
              style={{
                fontStyle: "italic",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Service.
            </span>
          </h1>

          <p
            style={{
              fontSize: "clamp(0.95rem, 1.8vw, 1.1rem)",
              lineHeight: 1.8,
              color: "#6b7280",
              maxWidth: "500px",
              margin: "0 auto 2rem",
              transform: heroVisible ? "translateY(0)" : "translateY(18px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.7s ease 0.35s, opacity 0.7s ease 0.35s",
            }}
          >
            Plain language. No tricks. Here's exactly what you agree to when you
            use NORYQ. Governed by Indian law.
          </p>

          {/* Last updated pill */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background: "rgba(102,126,234,0.04)",
              border: "1px solid #ede9fe",
              borderRadius: "999px",
              padding: "0.35rem 1rem",
              transform: heroVisible ? "translateY(0)" : "translateY(14px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.6s ease 0.5s, opacity 0.6s ease 0.5s",
            }}
          >
            <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
              Last updated:{" "}
              <span style={{ color: "#7c3aed", fontWeight: 600 }}>
                {LAST_UPDATED}
              </span>
            </span>
          </div>
        </div>

        <div
          style={{
            height: "2px",
            background:
              "linear-gradient(90deg, transparent, #c4b5fd 40%, #a78bfa 60%, transparent)",
          }}
        />
      </div>

      {/* ── QUICK NAV PILLS ──────────────────────────────────────────── */}
      <div
        className="tos-quicknav"
        style={{
          background: "#fff",
          borderBottom: "1px solid #ede9fe",
          padding: "1.25rem 2rem",
          overflowX: "auto",
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            display: "flex",
            gap: "0.6rem",
            flexWrap: "wrap",
          }}
        >
          {navLabels.map((label, i) => (
            <button
              key={i}
              onClick={() =>
                document
                  .querySelectorAll("[data-section]")
                  [i]?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
              style={{
                background: "rgba(102,126,234,0.05)",
                border: "1px solid #ede9fe",
                borderRadius: "999px",
                padding: "0.4rem 1rem",
                fontSize: "0.78rem",
                color: "#7c3aed",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                letterSpacing: "0.04em",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "linear-gradient(135deg,rgba(102,126,234,0.1),rgba(118,75,162,0.14))";
                e.currentTarget.style.borderColor = "#c4b5fd";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(102,126,234,0.05)";
                e.currentTarget.style.borderColor = "#ede9fe";
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── SECTIONS ─────────────────────────────────────────────────── */}
      <div
        className="tos-sections"
        style={{ maxWidth: "900px", margin: "0 auto", padding: "4rem 2rem" }}
      >
        {sections.map((section, idx) => (
          <SectionCard key={idx} section={section} idx={idx} />
        ))}

        {/* ── CTA ──────────────────────────────────────────────────── */}
        <div
          ref={ctaRef}
          className="tos-cta-card"
          style={{
            background: "#fff",
            border: "1.5px solid #ede9fe",
            borderRadius: "20px",
            padding: "3.5rem 2.5rem",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 4px 40px rgba(102,126,234,0.08)",
            marginTop: "2rem",
            transform: ctaVisible ? "translateY(0)" : "translateY(24px)",
            opacity: ctaVisible ? 1 : 0,
            transition:
              "transform 0.7s cubic-bezier(.22,1,.36,1), opacity 0.7s ease",
          }}
        >
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "70%",
              height: "100px",
              background:
                "radial-gradient(ellipse, rgba(118,75,162,0.07) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "16px",
              margin: "0 auto 1.5rem",
              background:
                "linear-gradient(135deg,rgba(102,126,234,0.1),rgba(118,75,162,0.16))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#7c3aed",
            }}
          >
            <FileText size={24} />
          </div>
          <h3
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(1.4rem, 3vw, 2rem)",
              fontWeight: "900",
              letterSpacing: "-0.02em",
              color: "#1e1b4b",
              marginBottom: "0.75rem",
            }}
          >
            Still have{" "}
            <span
              style={{
                fontStyle: "italic",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              questions?
            </span>
          </h3>
          <p
            style={{
              color: "#6b7280",
              lineHeight: 1.7,
              maxWidth: "400px",
              margin: "0 auto 2rem",
              fontSize: "0.95rem",
            }}
          >
            Write to our Grievance Officer. We acknowledge within 24 hours and
            resolve within 15 days.
          </p>
          <a href="/contact" style={{ textDecoration: "none" }}>
            <button
              className="tos-cta-btn"
              style={{
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                color: "#fff",
                border: "none",
                padding: "1rem 2.5rem",
                borderRadius: "12px",
                fontSize: "0.9rem",
                fontWeight: "700",
                letterSpacing: "0.06em",
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: "0 8px 28px rgba(102,126,234,0.28)",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 16px 44px rgba(118,75,162,0.38)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 8px 28px rgba(102,126,234,0.28)";
              }}
            >
              Email Legal Team →
            </button>
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfService;
